// src/context/GamepadProvider.tsx

import React, { useRef, useState } from 'react';
import {
  GamepadButtonEvent,
  GamepadType,
  UniversalButton,
  GamepadAxisEvent,
} from '../types/Gamepad';
import { useGamepad } from '../hooks/useGamepad';
import { GamepadContext } from './GamepadContext';
import { universalButtonMap } from '../utils/universalButtonMap';
import {
  UNDO_SYNTH_BUTTON_INDEX,
  evaluateUndoAxis,
  resolveUndoAxisIndex,
} from '../utils/gamepadUndo';
import SelectGamepadModal from '../components/ora/Gamepad/SelectGamepadModal';

interface GamepadProviderProps {
  children: React.ReactNode;
}

export const GamepadProvider: React.FC<GamepadProviderProps> = ({
  children,
}) => {
  const [lastButtonEvent, setLastButtonEvent] =
    useState<GamepadButtonEventWithLogical | null>(null);

  const [gamepadType, setGamepadType] = useState<GamepadType | null>(null);

  const [isSelectGamepadModalOpen, setIsSelectGamepadModalOpen] =
    useState(false);

  const previousButtonEventRef = useRef<GamepadButtonEventWithLogical | null>(
    null
  );

  // Scroll target state
  const scrollTargetRef = useRef<HTMLElement | Window>(window);
  // Tracks whether the 8BitDo d-pad "undo rating" axis is currently held, so a
  // single press emits one undo event rather than one per polled frame.
  const undoAxisEngagedRef = useRef(false);

  // Emit a synthetic "undo rating" button event so consumers can treat the d-pad
  // gesture like any other logical button (see universalButtonMap for Xbox/PS).
  const emitUndoButtonEvent = () => {
    setLastButtonEvent({
      buttonIndex: UNDO_SYNTH_BUTTON_INDEX,
      pressed: true,
      universalButton: 'UNDO',
    });
  };

  // Handle button events
  const handleButtonEvent = (event: GamepadButtonEvent) => {
    const previousEvent = previousButtonEventRef.current;

    const isNewEvent =
      !previousEvent ||
      previousEvent.buttonIndex !== event.buttonIndex ||
      previousEvent.pressed !== event.pressed;

    if (isNewEvent) {
      const mapping = universalButtonMap[event.buttonIndex];

      const universalButton = mapping
        ? mapping[gamepadType as GamepadType]
        : null;

      const eventWithLogical: GamepadButtonEventWithLogical = {
        ...event,
        universalButton: universalButton || null,
      };

      setLastButtonEvent(eventWithLogical);
      previousButtonEventRef.current = eventWithLogical;
    }
  };

  // Handle axis events for scrolling
  const handleAxisEvent = (event: GamepadAxisEvent) => {
    const { axisIndex, value } = event;
    const deadZone = 0.1;
    const scrollSpeed = 20;
    // 8BitDo controllers report the d-pad as axes, so "undo" arrives here rather
    // than as a button. The axis not reserved for scrolling carries undo:
    // vertical → UP (axis 1), horizontal → LEFT (axis 0).
    const undoAxisIndex = resolveUndoAxisIndex(gamepadType);
    if (undoAxisIndex !== null && axisIndex === undoAxisIndex) {
      const { trigger, engaged } = evaluateUndoAxis(
        value,
        undoAxisEngagedRef.current
      );

      undoAxisEngagedRef.current = engaged;
      if (trigger) {
        emitUndoButtonEvent();
      }
    }

    // if gamepad is 8bitvertical, use left and right buttons to scroll
    if (gamepadType === '8bitVertical') {
      if (axisIndex === 0 && Math.abs(value) > deadZone) {
        const scrollAmount = value * scrollSpeed * -1;
        const target = scrollTargetRef.current;
        if (target instanceof Window) {
          window.scrollBy(0, scrollAmount);
        } else {
          target.scrollBy({ top: scrollAmount, behavior: 'auto' });
        }
      }
    } else {
      if (axisIndex === 1 && Math.abs(value) > deadZone) {
        const scrollAmount = value * scrollSpeed;
        const target = scrollTargetRef.current;
        if (target instanceof Window) {
          window.scrollBy(0, scrollAmount);
        } else {
          target.scrollBy({ top: scrollAmount, behavior: 'auto' });
        }
      }
    }
  };

  // Method to set the scroll target
  const setScrollTarget = (target: HTMLElement | Window | null) => {
    scrollTargetRef.current = target || window;
  };

  const handleGamepadConnected = () => {
    if (gamepadType || isSelectGamepadModalOpen) return;
    setIsSelectGamepadModalOpen(true);
  };

  const handleGamepadDisconnected = () => {
    setIsSelectGamepadModalOpen(false);
    setGamepadType(null);
  };

  useGamepad({
    onButtonEvent: handleButtonEvent,
    onAxisEvent: handleAxisEvent,
    onGamepadConnected: handleGamepadConnected,
    onGamepadDisconnected: handleGamepadDisconnected,
  });

  return (
    <GamepadContext.Provider
      value={{
        lastButtonEvent,
        gamepadType,
        setScrollTarget,
        isSelectGamepadModalOpen,
        setIsSelectGamepadModalOpen,
      }}
    >
      {children}
      <SelectGamepadModal
        isOpen={isSelectGamepadModalOpen}
        setIsOpen={setIsSelectGamepadModalOpen}
        gamepadType={gamepadType}
        setGamepadType={setGamepadType}
      />
    </GamepadContext.Provider>
  );
};

// Extend GamepadButtonEvent to include universalButton
interface GamepadButtonEventWithLogical extends GamepadButtonEvent {
  universalButton: UniversalButton | null;
}
